import { useState } from "react";
import { toast } from "sonner";
import { Send, Route as RouteIcon } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { api, formatApiError } from "@/lib/api";
import { IMAGES } from "@/lib/site";

export default function Devis() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", pickup: "", dropoff: "", date: "", passengers: "2", message: "",
  });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.pickup || !form.dropoff) {
      toast.error("Merci d'indiquer nom, email, départ et destination.");
      return;
    }
    setSending(true);
    try {
      await api.post("/quotes", { ...form, passengers: parseInt(form.passengers, 10) });
      toast.success("Demande envoyée ! Nous revenons vers vous avec un devis personnalisé.");
      setForm({ name: "", email: "", phone: "", pickup: "", dropoff: "", date: "", passengers: "2", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'envoi.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="devis-page" className="pt-32 pb-24">
      <section className="mx-auto max-w-7xl px-5 lg:px-10">
        <Reveal>
          <p className="text-gold text-xs tracking-[0.4em] uppercase">Sur mesure</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-white">Devis Longue Distance</h1>
          <p className="mt-5 max-w-2xl text-white/60">
            Province, stations de ski, châteaux, destinations européennes : décrivez votre trajet, nous vous établissons un
            devis personnalisé sans engagement.
          </p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-[1fr_360px] gap-12">
          <Reveal>
            <form onSubmit={submit} data-testid="devis-form" className="rounded-2xl border border-white/10 bg-panel p-8 lg:p-10 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-white/70">Nom complet *</Label>
                  <Input data-testid="devis-name" value={form.name} onChange={set("name")} className="mt-2 bg-ink border-white/15 focus-visible:ring-gold" />
                </div>
                <div>
                  <Label className="text-white/70">Téléphone</Label>
                  <Input data-testid="devis-phone" value={form.phone} onChange={set("phone")} className="mt-2 bg-ink border-white/15 focus-visible:ring-gold" />
                </div>
              </div>
              <div>
                <Label className="text-white/70">Email *</Label>
                <Input data-testid="devis-email" type="email" value={form.email} onChange={set("email")} className="mt-2 bg-ink border-white/15 focus-visible:ring-gold" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-white/70">Départ *</Label>
                  <div className="mt-2">
                    <AddressAutocomplete testid="devis-pickup" value={form.pickup} onChange={(v) => setForm((f) => ({ ...f, pickup: v }))} onSelect={(a) => setForm((f) => ({ ...f, pickup: a.label }))} placeholder="Adresse de départ" />
                  </div>
                </div>
                <div>
                  <Label className="text-white/70">Destination *</Label>
                  <div className="mt-2">
                    <AddressAutocomplete testid="devis-dropoff" value={form.dropoff} onChange={(v) => setForm((f) => ({ ...f, dropoff: v }))} onSelect={(a) => setForm((f) => ({ ...f, dropoff: a.label }))} placeholder="Adresse d'arrivée" />
                  </div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-white/70">Date souhaitée</Label>
                  <Input data-testid="devis-date" type="date" value={form.date} onChange={set("date")} className="mt-2 bg-ink border-white/15 focus-visible:ring-gold" />
                </div>
                <div>
                  <Label className="text-white/70">Passagers</Label>
                  <Select value={form.passengers} onValueChange={(v) => setForm((f) => ({ ...f, passengers: v }))}>
                    <SelectTrigger data-testid="devis-passengers" className="mt-2 bg-ink border-white/15 focus:ring-gold"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-white/70">Détails du trajet</Label>
                <Textarea data-testid="devis-message" value={form.message} onChange={set("message")} rows={4} className="mt-2 bg-ink border-white/15 focus-visible:ring-gold" placeholder="Aller-retour, horaires, bagages, attente sur place…" />
              </div>
              <button type="submit" disabled={sending} data-testid="devis-submit" className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-medium text-black transition-colors duration-300 hover:bg-goldsoft disabled:opacity-60">
                {sending ? "Envoi…" : "Demander mon devis"} <Send size={16} />
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="rounded-2xl overflow-hidden border border-white/10 h-full min-h-[300px] relative">
              <img src={IMAGES.nightDrive} alt="Trajet longue distance de nuit" className="absolute inset-0 h-full w-full object-cover opacity-60" />
              <div className="relative z-10 p-8 h-full flex flex-col justify-end bg-gradient-to-t from-ink to-transparent">
                <RouteIcon size={26} className="text-gold" />
                <h3 className="mt-4 font-serif text-2xl text-white">Aucune limite de distance</h3>
                <p className="mt-2 text-sm text-white/70">Tarification transparente au kilomètre, chauffeur dédié et véhicule adapté à votre voyage.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
